import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import { useDispatch, useSelector } from "react-redux";
import {
  PrimaryButton,
  SecondaryButton,
  SecondaryDangerButton,
} from "../../components/Buttons.jsx";
import {
  createCustomProduct,
  getAvailableCategories,
  getCustomCategories,
  getCustomProduct,
  updateCustomProduct,
} from "../../api/inventory.js";
import { FaCamera, FaCloudArrowUp, FaX } from "react-icons/fa6";
import { canvasPreview, useDebounceEffect, initializeCrop, convertCropToPx, createCroppedBlob } from "../utility/image.jsx";
import { formatValue } from "../../utils/Utils.js";
import { FaPlus } from "react-icons/fa6";
import { uploadFile } from "../../api/index.js";
import { addToast } from "../../store/app/action.js";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Tooltip from "../../components/Tooltip.jsx";
import DropdownMulti from "../../components/DropdownMulti.jsx";
import ModalBasic from "../../components/ModalBasic.jsx";

const SCREENING_FLAG_LABELS = [
  ["is_vape", "Vape product"],
  ["is_flavored_vape", "Flavored vape product"],
  ["contains_hemp_derived_cannabinoid", "Hemp-derived cannabinoid content"],
  ["is_weapon", "Weapon or weapon-related item"],
  ["is_illegal_substance", "Possible illegal substance"],
  ["is_prostitution_service", "Possible prostitution-related service"],
];

function getScreeningReasons(screening) {
  if (!screening || typeof screening !== "object") return [];
  return SCREENING_FLAG_LABELS.filter(([key]) => screening[key]).map(
    ([, label]) => label
  );
}

let selectedClass =
  "inline-flex items-center justify-center text-sm font-medium leading-5 rounded-full px-3 py-1 border border-transparent shadow-sm bg-theme-button-primary text-theme-button-primary-text duration-150 ease-in-out";
let unselectedClass =
  "inline-flex items-center justify-center text-sm font-medium leading-5 rounded-full px-3 py-1 border border-theme-button-primary hover:bg-theme-button-secondary-hover shadow-sm bg-transparent text-theme-button-primary duration-150 ease-in-out";

const CustomProduct = ({
  embedded = false,
  posProduct: posProductProp = null,
  onCreated = null,
  onCancel = null,
} = {}) => {
  const { selectedLocation, selectedMerchant } = useSelector(
    (state) => state.app
  );
  const id = useParams().sku_id;
  const location = useLocation();
  const urlParams = new URLSearchParams(window.location.search);
  const [returnView] = useState(urlParams.get("view"));
  if (returnView && !embedded) {
    window.history.replaceState({}, document.title, window.location.pathname);
  }

  const [publishToLocation, setPublishToLocation] = useState([]);
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const dispatch = useDispatch();
  const [categories, setCategories] = useState([]);
  const imageUploadRef = useRef(null);
  const [image, setImage] = useState(null);
  const [crop, setCrop] = useState();
  const [cropDisabled, setCropDisabled] = useState(false);
  const [previewView, setPreviewView] = useState("mainMenu");
  const [customCategories, setCustomCategories] = useState([]);
  const [selectedCustomCategories, setSelectedCustomCategories] = useState([]);

  const mainPageCanvasRef = useRef(null);
  const categoryPageCanvasRef = useRef(null);
  const modalCanvasRef = useRef(null);
  const imageRef = useRef(null);
  const [existingProduct, setExistingProduct] = useState(null);
  const [pendingReviewModal, setPendingReviewModal] = useState(null);

  const [info, setInfo] = useState({ status: "published" });
  const isPosControlled = embedded || location.state?.fromProductMapping;
  
  // Pre-populate form from verified product or from Product Mapping (POS product) if available
  useEffect(() => {
    if (id) return; // Don't pre-populate if editing existing product
    
    const verifiedProduct = location.state?.fromVerifiedProduct;
    const fromProductMapping = location.state?.fromProductMapping;
    const posProduct = location.state?.posProduct;

    if (verifiedProduct) {
      setInfo((prevInfo) => ({
        ...prevInfo,
        status: 'published',
        title: verifiedProduct.title || "",
        company: verifiedProduct.company || "",
        description: verifiedProduct.description || "",
        volume_label: verifiedProduct.volume_label || "",
        upc_code: verifiedProduct.upc_code || "",
        price: "$x.xx",
      }));
      
      // Set image if available
      if (verifiedProduct.image) {
        setImage(verifiedProduct.image);
        setCropDisabled(true);
        setCrop({
          unit: "%",
          width: 100,
          height: 100,
          x: 0,
          y: 0,
        });
      }
    } else if (fromProductMapping && posProduct?.pos_name) {
      setInfo((prevInfo) => ({
        ...prevInfo,
        status: "published",
        title: posProduct.pos_name || "",
        volume_label: prevInfo.volume_label || "",
        upc_code: posProduct.pos_upc || prevInfo.upc_code || "",
      }));
    } else if (embedded && posProductProp?.pos_name) {
      setInfo((prevInfo) => ({
        ...prevInfo,
        status: "published",
        title: posProductProp.pos_name || "",
        volume_label: prevInfo.volume_label || "",
        upc_code: posProductProp.pos_upc || prevInfo.upc_code || "",
      }));
    }
  }, [id, location.state, embedded, posProductProp?.pos_name]);
  
  useEffect(() => {
    if (!id) return;
    getCustomProduct(id).then((data) => {
      const product = data.product;
      setSelectedCustomCategories(data.custom_categories || [])
      setInfo({
        category_id: product.default_category,
        title: product.title,
        company: product.company,
        description: product.description,
        volume_label: product.volume_label,
        upc_code: product.upc_code,
        price: "$x.xx",
      });
      setCropDisabled(true);
      setExistingProduct(product);
      setImage(product.image);
      setCrop({
        unit: "%",
        width: 100,
        height: 100,
        x: 0,
        y: 0,
      });
    });
  }, [id]);
  useEffect(() => {
    setPublishToLocation([selectedLocation.store_id]);
  }, [selectedLocation]);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    setCropDisabled(false);

    function onImageLoad(e) {
      const { naturalWidth: width, naturalHeight: height } = e.currentTarget;
      const crop = initializeCrop(width, height, 1);
      setCrop(crop);
    }

    reader.onload = (e) => {
      setImage(e.target.result);
      // get image size
      const img = new Image();
      img.src = e.target.result;
      img.onload = onImageLoad;
    };
    reader.readAsDataURL(file);
  }, []);

  useDebounceEffect(
    async () => {
      if (
        crop?.width &&
        crop?.height &&
        imageRef.current &&
        mainPageCanvasRef.current &&
        categoryPageCanvasRef.current
      ) {
        let toUse = convertCropToPx(crop, imageRef.current.width, imageRef.current.height);
        if (crop.unit === "%") {
          setCrop(toUse);
        }
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(imageRef.current, mainPageCanvasRef.current, toUse, 1, 0);
        canvasPreview(
          imageRef.current,
          categoryPageCanvasRef.current,
          toUse,
          1,
          0
        );
        canvasPreview(imageRef.current, modalCanvasRef.current, toUse, 1, 0);
      }
    },
    100,
    [
      crop,
      imageRef.current,
      mainPageCanvasRef.current,
      categoryPageCanvasRef.current,
    ]
  );

  useEffect(() => {
    if (!selectedLocation) return;
    getAvailableCategories().then((data) => {
      setCategories(data.categories);
    });
    getCustomCategories().then((data) => {
      setCustomCategories(
        data.categories?.filter?.(c => !c.is_dynamic).map?.((c) => ({ label: c.name, id: c._id }))
      );
    });
  }, [selectedLocation]);
  useEffect(() => {
    if (!categories || categories.length === 0) return;
    
    setInfo((prevInfo) => {
      // If we already have a category_id set, just find the category object
      if (prevInfo?.category_id) {
        const category = categories.find((c) => c.id === prevInfo.category_id);
        if (category) {
          return {
            ...prevInfo,
            category: category,
          };
        }
      }
      
      // Check if we have a verified product category to use
      const verifiedProduct = location.state?.fromVerifiedProduct;
      if (verifiedProduct?.default_category && !id) {
        // Try to find category by name first
        const categoryByName = categories.find(
          (c) => c.name === verifiedProduct.default_category && c.type !== "divider"
        );
        if (categoryByName) {
          return {
            ...prevInfo,
            category_id: categoryByName.id,
            category: categoryByName,
          };
        }
      }
      
      // Default: find first non-divider category
      const defaultCategory = categories.find((c) => c.type !== "divider");
      if (defaultCategory) {
        return {
          ...prevInfo,
          category: defaultCategory,
        };
      }
      
      return prevInfo;
    });
  }, [categories, id, location.state]);

  if (!selectedLocation) {
    return null;
  }

  const submit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    const formData = new FormData(e.target);
    const formFields = [
      "default_category",
      "title",
      "description",
      "company",
      "volume_label",
      "price",
      "cost",
      "status",
      "upc_code",
    ];


    const numberFields = ["price", "cost"];
    const submitObj = {};
    formFields.forEach((field) => {
      submitObj[field] = formData.get(field);
      if (numberFields.includes(field)) {
        submitObj[field] = parseFloat(submitObj[field]);
      }
    });

    let requiredFields = ["default_category", "title", "volume_label", "price"];
    if (id || isPosControlled) {
      requiredFields = requiredFields.filter((field) => field !== "price");
    }
    const friendly = {
      default_category: "Category",
      title: "Product Name",
      volume_label: "Volume / Size",
      price: "Price",
    }
    for (const field of requiredFields) {
      if (!submitObj[field]) {
        dispatch(
          addToast({
            type: "error",
            message: `${friendly[field]} is required`,
          })
        );
        setProcessing(false);
        return;
      }
    }

    if (publishToLocation.length === 0 && !id && submitObj.status === "published") {
      dispatch(
        addToast({
          type: "error",
          message: "Please select at least one location to publish to",
        })
      );
      setProcessing(false);
      return;
    }
    if(submitObj.status === "published"){
      submitObj.store_ids = publishToLocation;
    }

    if (selectedCustomCategories.length === 0) {
      dispatch(
        addToast({
          type: "error",
          message: "Please select at least one display category",
        })
      );
      setProcessing(false);
      return;
    }
    submitObj.custom_categories = selectedCustomCategories;

    if (!id && !isPosControlled && submitObj.status === "published" && !submitObj.price) {
      dispatch(
        addToast({
          type: "error",
          message: "Price is required for published products",
        })
      );
      setProcessing(false);
      return;
    }
    submitObj.volume = submitObj.volume_label;

    if (crop && image?.startsWith("data:image")) {
      const image = imageRef.current;
      if (image && crop) {
        const blob = await createCroppedBlob(image, crop, 1000, 1000, 0.9);
        const uploadResponse = await uploadFile(blob, "product");
        submitObj.image_large = uploadResponse.url;
        submitObj.image = uploadResponse.url;
      }
    } else if (existingProduct) {
      submitObj.image = existingProduct.image;
      submitObj.image_large = existingProduct.image_large;
    } else if (image && !image.startsWith("data:image") && !id) {
      // Handle pre-populated image from verified product (URL string)
      submitObj.image = image;
      submitObj.image_large = image;
    }

    try {
      let additional = {};
      if (id) {
        await updateCustomProduct(id, submitObj);
        const selectedCustomCategoryNames =   selectedCustomCategories.map((id) => customCategories.find((c) => c.id === id).label)
        additional.updated = {
          [id]: {
            id,
            ...submitObj,
            default_category: selectedCustomCategoryNames,
            merchant_id: selectedMerchant.id,
            sku_id: id,
          },
        };
      } else {
        const res = await createCustomProduct(submitObj);
        additional.created = {
          id: res.product.sku_id,
          ...submitObj,
          default_category: info.category?.name,
          merchant_id: selectedMerchant.id,
          sku_id: res.product.sku_id,
        };

        const completeNewProductFlow = () => {
          // If embedded (e.g. modal from Product Mapping), call onCreated and stay
          if (onCreated) {
            onCreated(res.product.sku_id);
            return;
          }
          // If we came from Product Mapping (navigated away), return there so the mapping can be applied
          const fromProductMapping = location.state?.fromProductMapping;
          const returnTo = location.state?.returnTo;
          const posProduct = location.state?.posProduct;
          if (fromProductMapping && returnTo && posProduct?.pos_id) {
            navigate(returnTo, {
              state: {
                mapPosIdToSku: {
                  posId: posProduct.pos_id,
                  skuId: res.product.sku_id,
                },
              },
            });
            return;
          }
          navigate(`/catalog/products?view=${returnView ?? "custom"}`, {
            state: additional,
          });
        };

        if (res.pending_review) {
          setPendingReviewModal({
            screening: res.screening,
            onContinue: completeNewProductFlow,
          });
          return;
        }
        completeNewProductFlow();
        return;
      }
      navigate(`/catalog/products?view=${returnView ?? "custom"}`, {
        state: additional,
      });
    } catch (e) {
      let message = "please try again";
      try {
        const serverMessage = e.response?.data?.error;
        if (serverMessage) {
          message = serverMessage;
        }
      } catch {}

      const errorStart = id
        ? "Error updating product, "
        : "Error creating product, ";
      dispatch(
        addToast({
          type: "error",
          message: errorStart + message,
          timer: 5,
        })
      );
    } finally {
      setProcessing(false);
    }
    };

  const dismissPendingReview = () => {
    setPendingReviewModal((current) => {
      if (current?.onContinue) {
        current.onContinue();
      }
      return null;
    });
  };

  const pendingReviewReasons = pendingReviewModal
    ? getScreeningReasons(pendingReviewModal.screening)
    : [];

  return (
    <div className={embedded ? "px-4 py-4 w-full" : "px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto"}>
      {/* Page header */}
      <div className={`sm:flex sm:justify-between sm:items-center ${embedded ? "mb-4" : "mb-8"}`}>
        {/* Left: Title */}
        <div className="mb-4 sm:mb-0">
          <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
            {id ? "Update Product" : "Create New Product"}
          </h1>
        </div>
        {embedded && onCancel && (
          <SecondaryButton
            type="button"
            onClick={onCancel}
            naked
            additionalClasses="text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 !p-0 !h-auto"
          >
            <FaX className="w-5 h-5" />
          </SecondaryButton>
        )}
      </div>

      <form onSubmit={submit}>
        <div className="flex gap-4">
          <div className="w-1/2 space-y-4 ">
            {/*   product form, with category dropdown, brand text field, product name text field, and description text area*/}
            <div>
              <div className="flex gap-2 items-center">
                Saucey Category{" "}
                <Tooltip position="bottom">
                  <div className="text-xs whitespace-nowrap">
                    Select the closest item categorization, <br />
                    this is required for sales tax calculation purpose.
                  </div>
                </Tooltip>
              </div>
              <div className="flex items-center relative">
                <select
                  value={info.category_id}
                  id="category"
                  name="default_category"
                  className="form-select w-full"
                  onChange={(e) => {
                    setInfo({
                      ...info,
                      category_id: e.target.value,
                      category: categories.find((c) => c.id === e.target.value),
                    });
                  }}
                >
                  {categories.map((category, idx) => {
                    const deliveryRestrictedArray =
                      category.direct_delivery_georestriction || [];
                    const generallyRestrictedArray =
                      category.direct_georestriction || [];

                    const deliveryRestricted = deliveryRestrictedArray.includes(
                      selectedLocation.state
                    );
                    const generallyRestricted =
                      generallyRestrictedArray.includes(selectedLocation.state);

                    if (category.type === "divider") {
                      return (
                        <option key={idx} value={category.id} disabled>
                          --- {category.name} ---
                        </option>
                      );
                    }
                    return (
                      <option
                        key={idx}
                        value={category.id}
                        disabled={generallyRestricted}
                      >
                        {category.name}{" "}
                        {generallyRestricted
                          ? ` (Not available in ${selectedLocation.state})`
                          : deliveryRestricted
                          ? ` (Delivery not available in ${selectedLocation.state})`
                          : ""}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <div>
              <div className="flex gap-2 items-center">
                Display Category{" "}
                <Tooltip position="bottom">
                  <div className="text-xs whitespace-nowrap">
                    Select the categories you wish to show your item in. <br />
                    You can create more categories in Catalog {">"} Categories.
                  </div>
                </Tooltip>
              </div>
              <div className="flex items-center relative">
                <DropdownMulti
                  options={customCategories}
                  selectedValues={selectedCustomCategories}
                  setSelectedValues={setSelectedCustomCategories}
                />
              </div>
            </div>

            <div>
              <div>Brand</div>
              <input
                name="company"
                placeholder="Enter brand name"
                className="form-input mt-2 w-full"
                value={info.company}
                onChange={(e) => setInfo({ ...info, company: e.target.value })}
              />
            </div>
            <div>
              <div>Product name</div>
              <input
                name="title"
                placeholder="Enter product name"
                className="form-input mt-2 w-full"
                value={info.title}
                onChange={(e) => setInfo({ ...info, title: e.target.value })}
              />
            </div>
            {/* Volume / Size */}
            <div>
              <div>Volume / Size</div>
              <input
                name="volume_label"
                placeholder="Enter Volume / Size"
                className="form-input mt-2 w-full"
                value={info.volume_label}
                onChange={(e) =>
                  setInfo({ ...info, volume_label: e.target.value })
                }
              />
            </div>
            <div>
              <div>Description</div>
              <textarea
                name="description"
                rows={5}
                placeholder="Enter Description"
                className="form-input mt-2 w-full resize-none"
                value={info.description}
                onChange={(e) =>
                  setInfo({ ...info, description: e.target.value })
                }
              />
            </div>
            <div>
              <div>UPC / Product Code (Optional)</div>
              <input
                name="upc_code"
                placeholder="Enter UPC"
                className="form-input mt-2 w-full resize-none"
                value={info.upc_code}
                onChange={(e) => setInfo({ ...info, upc_code: e.target.value })}
              />
            </div>
            <div className="flex gap-4 items-start flex-col">
              <div>
                {image && (
                  <SecondaryButton
                    onClick={(e) => {
                      e.preventDefault();
                      imageUploadRef.current.click();
                    }}
                  >
                    Replace Image
                  </SecondaryButton>
                )}{" "}
                {image && cropDisabled && "Upload new image to resize"}
              </div>

              <div className="relative h-[300px] w-[500px] border-slate-200 hover:border-slate-300 border rounded-lg">
                {!image ? (
                  <>
                    <FaCloudArrowUp className="text-8xl opacity-10 absolute left-1/2 -translate-x-1/2 top-[50%]" />
                    <div className="absolute text-lg h-[40px] w-[30px] bg-slate-300 rounded-md flex justify-center items-center pt-1 left-1/2 -translate-x-[45%] top-[22%] rotate-12">
                      <FaCamera className="opacity-70 text-white" />
                    </div>
                    <div className="absolute text-lg h-[40px] w-[30px] bg-slate-200 rounded-md flex justify-center items-center pt-1 left-1/4 -translate-x-1/2 top-[42%] -rotate-[35deg]">
                      <FaCamera className="opacity-70 text-slate-400" />
                    </div>
                    <div className="absolute text-lg h-[40px] w-[30px] bg-slate-200 rounded-md flex justify-center items-center pt-1 left-3/4 -translate-x-1/2 top-[42%] rotate-[35deg]">
                      <FaCamera className="opacity-70 text-slate-400" />
                    </div>
                  </>
                ) : (
                  <ReactCrop
                    aspect={1}
                    crop={crop}
                    keepSelection
                    disabled={cropDisabled}
                    onChange={(c) => {
                      setCrop(c);
                    }}
                  >
                    <img ref={imageRef} src={image} />
                  </ReactCrop>
                )}
                {!image && (
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2  w-32">
                    <PrimaryButton
                      onClick={(e) => {
                        e.preventDefault();
                        imageUploadRef.current.click();
                      }}
                    >
                      Upload Image
                    </PrimaryButton>
                  </div>
                )}
              </div>
            </div>

            <input
              type="file"
              accept="image/*"
              ref={imageUploadRef}
              onChange={handleFileChange}
              name="icon"
              style={{ display: "none" }} // Hide the file input
            />
          </div>
          <div className="w-96">
            <div className="bg-white w-full p-6 rounded-lg space-y-4">
              {!id && (
                <>
                  <div>
                    <div>Status</div>
                    <div className="flex items-center relative">
                      <select
                        name="status"
                        id="status"
                        className={`form-select w-full ${isPosControlled ? "bg-slate-100 text-slate-500 cursor-not-allowed" : ""}`}
                        disabled={isPosControlled}
                        value={info.status}
                        onChange={(e) => {
                          setInfo({ ...info, status: e.target.value });
                        }}
                      >
                        <option value="published">Published</option>
                        <option value="unpublished">Unpublished</option>
                      </select>
                    </div>
                    {isPosControlled && (
                      <div className="mt-1 text-xs text-slate-500">
                        Status is controlled by your POS and cannot be changed here.
                      </div>
                    )}
                  </div>
                  {info.status === "published" &&
                    selectedMerchant.locations.length > 1 && (
                      <div>
                        <div className="flex gap-2 items-center">
                          <input
                            type="checkbox"
                            className="form-checkbox"
                            checked={
                              publishToLocation.length ===
                              selectedMerchant.locations.length
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setPublishToLocation(
                                  selectedMerchant.locations.map(
                                    (loc) => loc.store_id
                                  )
                                );
                              } else {
                                setPublishToLocation([]);
                              }
                            }}
                          />
                          <span>All Locations</span>
                        </div>
                        {selectedMerchant.locations.map((location) => (
                          <div
                            key={location.store_id}
                            className="flex gap-2 items-center"
                          >
                            <input
                              type="checkbox"
                              className="form-checkbox"
                              checked={publishToLocation.includes(
                                location.store_id
                              )}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setPublishToLocation((prev) => [
                                    ...prev,
                                    location.store_id,
                                  ]);
                                } else {
                                  setPublishToLocation((prev) =>
                                    prev.filter(
                                      (loc) => loc !== location.store_id
                                    )
                                  );
                                }
                              }}
                            />
                            <span>{location.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  <div>
                    <div>Price</div>
                    <div className="flex items-center relative">
                      <div className="absolute top-1/2 -translate-y-1/2 mt-1 ml-3">
                        $
                      </div>
                      <input
                        placeholder="Enter Price"
                        name="price"
                        className={`form-input mt-2 w-full !pl-8 ${isPosControlled ? "bg-slate-100 text-slate-500 cursor-not-allowed" : ""}`}
                        disabled={isPosControlled}
                        onChange={(e) => {
                          setInfo({
                            ...info,
                            price: formatValue(e.target.value),
                          });
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div>Cost (Optional)</div>
                    <div className="flex items-center relative">
                      <div className="absolute top-1/2 -translate-y-1/2 mt-1 ml-3">
                        $
                      </div>
                      <input
                        name="cost"
                        placeholder="Enter Cost (Optional)"
                        className={`form-input mt-2 w-full !pl-8 ${isPosControlled ? "bg-slate-100 text-slate-500 cursor-not-allowed" : ""}`}
                        disabled={isPosControlled}
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="flex flex-col gap-4 pt-4">
                <PrimaryButton loading={processing} width="full" type="submit">
                  {id ? "Update Product" : "Create Product"}
                </PrimaryButton>
              </div>
            </div>
            <div className="w-full mt-4">
              <div className="font-bold text-2xl">Preview</div>
              <div className="mb-12 mt-2">
                <ul className="flex flex-wrap -m-1">
                  <li className="m-1">
                    <button
                      type="button"
                      className={
                        previewView === "mainMenu"
                          ? selectedClass
                          : unselectedClass
                      }
                      onClick={() => setPreviewView("mainMenu")}
                    >
                      Main Menu
                    </button>
                  </li>
                  <li className="m-1">
                    <button
                      type="button"
                      className={
                        previewView === "categoryPage"
                          ? selectedClass
                          : unselectedClass
                      }
                      onClick={() => setPreviewView("categoryPage")}
                    >
                      Category Page
                    </button>
                  </li>
                  <li className="m-1">
                    <button
                      type="button"
                      className={
                        previewView === "productModal"
                          ? selectedClass
                          : unselectedClass
                      }
                      onClick={() => setPreviewView("productModal")}
                    >
                      Product Modal
                    </button>
                  </li>
                </ul>
              </div>
              <div className={previewView === "mainMenu" ? "block" : "hidden"}>
                <div>
                  <div className="pb-10">
                    <div className="font-bold text-xl float-left">
                      {info.category?.name}
                    </div>
                    <div className="float-right font-bold text-sm bg-slate-200 px-3 py-1 rounded-full">
                      18 items {"->"}
                    </div>
                  </div>
                  <div className="flex overflow-hidden w-full">
                    <div className="flex gap-2">
                      <div className="border-slate-300 h-[300px] w-[152px]  overflow-hidden rounded-xl border text-slate-800 text-[13px]">
                        <canvas
                          ref={mainPageCanvasRef}
                          style={{
                            objectFit: "contain",
                            width: 152,
                            height: 171,
                          }}
                        />
                        <div className="px-1 pt-3 space-y-2">
                          <div className="font-bold">{info.price}</div>
                          <div className="preview-tile-title">{info.title}</div>
                          <div>{info.volume_label}</div>
                        </div>
                      </div>
                      <div className="border-slate-300 h-[300px] w-[152px]  overflow-hidden rounded-xl border text-slate-800 text-[13px]">
                        <img
                          src="https://saucey.com/cdn-cgi/image/width=500,f=auto,q=90/https://cdn.saucey.com/production/products/TB-MARL-RED/image_large/64e0dc8d2ea27c96.jpg"
                          style={{
                            objectFit: "contain",
                            width: 152,
                            height: 171,
                          }}
                          className="blur-lg"
                        />
                        <div className="px-1 pt-3 space-y-2 blur-sm">
                          <div className="font-bold">$12.30</div>
                          <div className="preview-tile-title">
                            Lorem Ipsum Dolor Sit Amet
                          </div>
                          <div>1 Pack</div>
                        </div>
                      </div>
                      <div className="border-slate-300 h-[300px] w-[152px]  overflow-hidden rounded-xl border text-slate-800 text-[13px]">
                        <img
                          src="https://saucey.com/cdn-cgi/image/width=500,f=auto,q=90/https://cdn.saucey.com/staging/products/WW-BAREF-15L/image.jpg"
                          className="blur-lg"
                          style={{
                            objectFit: "contain",
                            width: 152,
                            height: 171,
                          }}
                        />
                        <div className="px-1 pt-3 space-y-2 blur-sm">
                          <div className="font-bold">$12.30</div>
                          <div className="preview-tile-title">
                            Lorem Ipsum Dolor Sit Amet
                          </div>
                          <div>3 Sticks</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={previewView === "categoryPage" ? "block" : "hidden"}
              >
                <div className="flex overflow-hidden w-full">
                  <div className="flex gap-4 flex-col">
                    <div className="border-slate-300 h-[115px] w-[338px]  overflow-hidden rounded-xl border text-slate-800 text-[13px] flex">
                      <div className="px-2 pt-1 space-y-1 flex flex-grow gap-1 flex-col">
                        <div className="font-bold">
                          {info.title}
                          <div className="font-normal">{info.volume_label}</div>
                        </div>
                        <div className="preview-tile-title">
                          {info.description}
                        </div>
                        <div className="font-bold">{info.price}</div>
                      </div>
                      <canvas
                        className="w-[111px]"
                        ref={categoryPageCanvasRef}
                        style={{
                          objectFit: "contain",
                          width: 115,
                          height: 115,
                        }}
                      />
                    </div>
                    <div className="border-slate-300 h-[125px] w-[338px]  overflow-hidden rounded-xl border text-slate-800 text-[13px] flex">
                      <div className="px-2 pt-1 space-y-1 flex flex-grow gap-1 flex-col blur-sm">
                        <div className="font-bold">
                          Lorem Ipsum Dolor Sit Amet
                          <div className="font-normal">1 pack</div>
                        </div>
                        <div className="preview-tile-title">
                          Lorem Ipsum Dolor Sit Amet Lorem Ipsum Dolor Sit Amet
                        </div>
                        <div className="font-bold">$12.99</div>
                      </div>
                      <img
                        src="https://saucey.com/cdn-cgi/image/width=500,f=auto,q=90/https://cdn.saucey.com/production/products/TB-MARL-RED/image_large/64e0dc8d2ea27c96.jpg"
                        style={{
                          objectFit: "contain",
                          width: 111,
                          height: 125,
                        }}
                        className="blur-lg"
                      />
                    </div>
                    <div className="border-slate-300 h-[125px] w-[338px]  overflow-hidden rounded-xl border text-slate-800 text-[13px] flex">
                      <div className="px-2 pt-1 space-y-1 flex flex-grow gap-1 flex-col blur-sm">
                        <div className="font-bold">
                          Lorem Ipsum Dolor Sit Amet
                          <div className="font-normal">1 pack</div>
                        </div>
                        <div className="preview-tile-title">
                          Lorem Ipsum Dolor Sit Amet Lorem Ipsum Dolor Sit Amet
                        </div>
                        <div className="font-bold">$12.99</div>
                      </div>
                      <img
                        src="https://saucey.com/cdn-cgi/image/width=500,f=auto,q=90/https://cdn.saucey.com/staging/products/WW-BAREF-15L/image.jpg"
                        style={{
                          objectFit: "contain",
                          width: 111,
                          height: 125,
                        }}
                        className="blur-lg"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={previewView === "productModal" ? "block" : "hidden"}
              >
                <div className="bg-slate-200 w-full h-[600px] flex items-center justify-center ">
                  <div className="bg-white w-[60%] full h-[450px] rounded-xl overflow-hidden flex flex-col overflow-y-auto">
                    <div className="p-2 flex justify-end w-full">
                      <FaX />
                    </div>
                    <canvas
                      ref={modalCanvasRef}
                      style={{
                        objectFit: "contain",
                        width: "100%",
                        height: "auto",
                      }}
                    />
                    <div className="py-4 px-2 flex-grow">
                      <div className="font-bold text-[14px]">{info.title}</div>
                      <div className="font-normal text-[10px] mb-2">
                        {info.volume_label}
                      </div>
                      <div className="font-normal text-[10px]">
                        {info.description}
                      </div>
                    </div>
                    {/*  bottom bar*/}

                    <div className="flex justify-between p-2 text-[10px] gap-2">
                      <div className="flex justify-center items-center text-slate-800 gap-1 bg-slate-200 rounded-full px-2">
                        <div className="pl-3">1</div>
                        <div>
                          <FaPlus />
                        </div>
                      </div>
                      <div className=" flex bg-[#14aa6e] text-white  p-1 rounded-full flex-1 justify-center">
                        Add to Cart | {info.price}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>

      <ModalBasic
        id="pending-review-custom-product"
        title="Product pending review"
        modalOpen={pendingReviewModal !== null}
        setModalOpen={(open) => {
          if (!open) dismissPendingReview();
        }}
        width="lg"
        zIndex={embedded ? 60 : 50}
      >
        <div className="space-y-4 text-sm text-slate-600 dark:text-slate-300">
          <p>
            Your product was saved and submitted for review. It is not live in
            the catalog until our team approves it.
          </p>
          <p>
            If we approve it, it will be published using the locations,
            display categories, and status you chose when you submitted this
            form.
          </p>
          {pendingReviewReasons.length > 0 ? (
            <div>
              <p className="font-medium text-slate-800 dark:text-slate-100 mb-2">
                Why it was flagged:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                {pendingReviewReasons.map((label) => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-slate-500 dark:text-slate-400">
              Our review team will verify this listing against our policies
              before it can be published.
            </p>
          )}
          <div className="pt-2 flex justify-end">
            <PrimaryButton type="button" onClick={dismissPendingReview}>
              Got it
            </PrimaryButton>
          </div>
        </div>
      </ModalBasic>
    </div>
  );
};

export default CustomProduct;
